import { fetchJson } from '../_shared/fetch-json';
import {
  EventMembersSchema,
  EventMemberRowSchema,
  PatchEventMemberResponseSchema,
  EventTablesSchema,
  EventViewSchema,
  EventViewsSchema,
  FollowUpRowsSchema,
  AddMemberBatchStatusSchema,
  CampaignMemberStatusesSchema,
  LauncherSfdcUsersSchema,
  type EventMember,
  type EventMemberRow,
  type EventTable,
  type EventView,
  type FollowUpRow,
  type AddMemberBatchRow,
  type AddMemberBatchStatus,
  type CampaignMemberStatus,
  type SfdcUser,
} from './event-manager-wire-schemas';

export type {
  EventMember,
  EventMemberRow,
  EventTable,
  EventView,
  FollowUpRow,
  AddMemberBatchRow,
  AddMemberBatchStatus,
  CampaignMemberStatus,
  SfdcUser,
};

type ApiFetch = (input: RequestInfo | URL, init?: RequestInit & { timeoutMs?: number }) => Promise<Response>;

// ── HTTP error contract ───────────────────────────────────────────────────────
//
// `apiFetch` here is the throwing mini-app boundary from
// `_shared/hooks/useApi.ts` (backed by `_shared/fetch-error.ts`): on any
// `!res.ok` it parses the problem+json body and throws a structured
// `FetchErrorLike` (`code`/`oauthUrl`/`userMessage`/`status`) BEFORE control
// returns here. That is why none of the functions below re-check `res.ok` or
// carry a local `throwFetchError` copy anymore — a failed response never
// reaches this file.

// ── Event Members ─────────────────────────────────────────────────────────────

export async function fetchEventMembers(apiFetch: ApiFetch, campaignId: string): Promise<EventMember[]> {
  return fetchJson(apiFetch, EventMembersSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/event-members`);
}

export async function initEventMembers(apiFetch: ApiFetch, campaignId: string): Promise<void> {
  await apiFetch(`/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/event-members/init`, {
    method: 'POST',
  });
}

export async function patchEventMember(
  apiFetch: ApiFetch,
  campaignId: string,
  memberId: number,
  fields: Partial<Pick<EventMember, 'role' | 'attending_status' | 'dietary_restrictions' | 'cal_invite_sent' | 'cal_invite_accepted' | 'owner_exclusion' | 'owner_priority' | 'intel_notes' | 'general_notes' | 'goer_notes' | 'follow_up_owner' | 'score' | 'recommendation' | 'reasoning' | 'approved' | 'gtme_notes' | 'primary_met_with' | 'secondary_met_with' | 'tertiary_met_with' | 'last_follow_up_date' | 'custom_fields'>> & { cm_status?: string },
): Promise<EventMemberRow> {
  return fetchJson(apiFetch, PatchEventMemberResponseSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/event-member/${memberId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
}

export async function patchEventMemberStatus(
  apiFetch: ApiFetch,
  campaignId: string,
  memberId: number,
  status: string,
): Promise<void> {
  await apiFetch(`/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/event-member/${memberId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}

export async function assignSeat(
  apiFetch: ApiFetch,
  campaignId: string,
  memberId: number,
  tableId: number | null,
  seatPosition?: number | null,
): Promise<EventMemberRow> {
  return fetchJson(apiFetch, EventMemberRowSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/event-member/${memberId}/seat`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ table_id: tableId, seat_position: seatPosition ?? null }),
  });
}

// ── Event Tables ──────────────────────────────────────────────────────────────

export async function fetchEventTables(apiFetch: ApiFetch, campaignId: string): Promise<EventTable[]> {
  return fetchJson(apiFetch, EventTablesSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/event-tables`);
}

export async function createEventTable(
  apiFetch: ApiFetch,
  campaignId: string,
  data: { table_name: string; capacity?: number; sort_order?: number },
): Promise<void> {
  await apiFetch(`/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/event-tables`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function patchEventTable(
  apiFetch: ApiFetch,
  campaignId: string,
  tableId: number,
  fields: Partial<Pick<EventTable, 'table_name' | 'capacity' | 'sort_order'>>,
): Promise<void> {
  await apiFetch(`/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/event-tables/${tableId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
}

export async function deleteEventTable(apiFetch: ApiFetch, campaignId: string, tableId: number): Promise<void> {
  await apiFetch(`/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/event-tables/${tableId}`, {
    method: 'DELETE',
  });
}

// ── Event Views ──────────────────────────────────────────────────────────────

export async function fetchViews(apiFetch: ApiFetch, campaignId: string): Promise<EventView[]> {
  return fetchJson(apiFetch, EventViewsSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/views`);
}

export async function createView(
  apiFetch: ApiFetch,
  campaignId: string,
  data: { name: string; columns: string[]; column_widths?: Record<string, number> },
): Promise<EventView> {
  return fetchJson(apiFetch, EventViewSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/views`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateView(
  apiFetch: ApiFetch,
  campaignId: string,
  viewId: number,
  fields: { name?: string; columns?: string[]; column_widths?: Record<string, number> },
): Promise<void> {
  await apiFetch(`/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/views/${viewId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
}

export async function deleteView(apiFetch: ApiFetch, campaignId: string, viewId: number): Promise<void> {
  await apiFetch(`/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/views/${viewId}`, {
    method: 'DELETE',
  });
}

// ── Follow-Up Data ───────────────────────────────────────────────────────────

export async function fetchFollowUpData(apiFetch: ApiFetch, campaignId: string): Promise<FollowUpRow[]> {
  return fetchJson(apiFetch, FollowUpRowsSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/follow-up`);
}

// ── Patch Campaign Details ───────────────────────────────────────────────
//
// Promoted to `mini-apps/_shared/launcher-campaign-schema.ts` (U6) alongside
// the `CampaignDetailSidebar` promotion, so `_shared`'s own
// `CampaignDetailSidebar.tsx` can import `PatchCampaignFields` without
// reaching into `mini-apps/launcher/**` (a lint-forbidden direction).
// Re-exported here so `EventManagerPage.tsx`/`LauncherDetailPage.tsx`/
// `LauncherListPage.tsx` keep their existing import path.
export type { PatchCampaignFields } from '../_shared/launcher-campaign-schema';
export { patchCampaignDetails } from '../_shared/launcher-campaign-schema';

// ── Add Campaign Member ──────────────────────────────────────────────────

export async function addCampaignMember(
  apiFetch: ApiFetch,
  campaignId: string,
  data: { full_name: string; work_email: string; status: string; notes?: string },
): Promise<void> {
  await apiFetch(`/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/add-member`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// ── Batch Status ─────────────────────────────────────────────────────────

export async function getAddMemberBatchStatus(
  apiFetch: ApiFetch,
  batchId: string,
): Promise<AddMemberBatchStatus> {
  return fetchJson(apiFetch, AddMemberBatchStatusSchema, `/api/tools/launcher/add-member-batches/${encodeURIComponent(batchId)}`);
}

// ── Cancel Batch ─────────────────────────────────────────────────────────

export async function cancelAddMemberBatch(
  apiFetch: ApiFetch,
  batchId: string,
): Promise<void> {
  await apiFetch(`/api/tools/launcher/add-member-batches/${encodeURIComponent(batchId)}/cancel`, {
    method: 'POST',
  });
}

// ── Campaign Member Statuses ─────────────────────────────────────────────────

export async function fetchCampaignMemberStatuses(apiFetch: ApiFetch, campaignId: string): Promise<CampaignMemberStatus[]> {
  return fetchJson(apiFetch, CampaignMemberStatusesSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/member-statuses`);
}

// ── SFDC Users ───────────────────────────────────────────────────────────────

export async function fetchSfdcUsers(apiFetch: ApiFetch): Promise<SfdcUser[]> {
  return fetchJson(apiFetch, LauncherSfdcUsersSchema, '/api/tools/launcher/sfdc-users');
}
